// GlobalSearch.jsx — ⌘K-style universal search across recipes, beans, pantry, favorites.
import { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, BookOpen, Coffee as CoffeeIcon, Package, Star } from "lucide-react";

export function GlobalSearch({ open, onClose, store, onNavigate }) {
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const out = [];

    for (const r of (store.recipes || [])) {
      if (matches(q, r.title, r.summary, ...(r.cuisine || []), ...(r.mood_tags || []))) {
        out.push({ type: "recipe", id: r.id, title: r.title, sub: (r.cuisine || []).join(", "), data: r });
      }
    }

    for (const b of (store.beans || [])) {
      if (matches(q, b.name, b.roaster, ...(b.origin || []))) {
        out.push({ type: "bean", id: b.id, title: b.name, sub: b.roaster || (b.origin || []).join(", "), data: b });
      }
    }

    for (const s of (store.skus || [])) {
      if (matches(q, s.name, s.brand, s.category)) {
        out.push({ type: "sku", id: s.id, title: s.name, sub: s.brand || s.category, data: s });
      }
    }

    for (const f of (store.favorites || [])) {
      if (matches(q, f.name, f.notes)) {
        out.push({ type: "favorite", id: f.id, title: f.name, sub: `${f.profile_id} · ${f.water_ml}g`, data: f });
      }
    }

    return out.slice(0, 12);
  }, [query, store.recipes, store.beans, store.skus, store.favorites]);

  useEffect(() => {
    if (selectedIdx >= results.length) setSelectedIdx(Math.max(0, results.length - 1));
  }, [results.length, selectedIdx]);

  const handleSelect = (r) => {
    if (!r) return;
    const tabFor = { recipe: "recipes", bean: "coffee", sku: "pantry", favorite: "coffee" };
    onNavigate(tabFor[r.type] || "daily");
    onClose();
  };

  const handleKey = (e) => {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx(i => Math.min(results.length - 1, i + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx(i => Math.max(0, i - 1)); }
    if (e.key === "Enter") { e.preventDefault(); handleSelect(results[selectedIdx]); }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-ink-700/40 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: -16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -16, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-xl bg-parchment-50 rounded-2xl shadow-2xl overflow-hidden border border-parchment-300"
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-parchment-200">
            <Search size={18} className="text-ink-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Search recipes, beans, pantry, favorites…"
              className="flex-1 bg-transparent text-base text-ink-700 placeholder:text-ink-400 focus:outline-none"
            />
            <button onClick={onClose} className="text-ink-400 hover:text-ink-600">
              <X size={16} />
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {results.length === 0 && query.trim() && (
              <div className="p-8 text-center text-ink-400 text-sm">
                Nothing found. Try different words.
              </div>
            )}
            {results.length === 0 && !query.trim() && (
              <div className="p-8 text-center text-ink-400 text-sm">
                Type to search across everything in Pronto.
                <div className="mt-2 text-[10px] font-mono uppercase tracking-wider">
                  ⌘K to open · Esc to close
                </div>
              </div>
            )}
            {results.map((r, i) => {
              const Icon = iconFor(r.type);
              return (
                <button
                  key={`${r.type}-${r.id}`}
                  onClick={() => handleSelect(r)}
                  onMouseEnter={() => setSelectedIdx(i)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left ${
                    selectedIdx === i ? "bg-parchment-100" : ""
                  }`}
                >
                  <Icon size={16} className="text-ink-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ink-700 truncate">{r.title}</div>
                    {r.sub && <div className="text-[11px] text-ink-400 truncate">{r.sub}</div>}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400">
                    {r.type}
                  </div>
                </button>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function matches(q, ...fields) {
  return fields.some(f => f && String(f).toLowerCase().includes(q));
}

function iconFor(type) {
  return type === "recipe" ? BookOpen
    : type === "bean" ? CoffeeIcon
    : type === "sku" ? Package
    : type === "favorite" ? Star
    : Search;
}
